import { useState, useEffect } from 'react';
import { industryService } from '../services/industryService';

/**
 * Professional theme hook with gradient support
 * Applies industry-specific colors with smooth gradients and proper depth
 */
export const useTheme = () => {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = async () => {
    try {
      setLoading(true);
      const themeData = await industryService.getMyTheme();
      setTheme(themeData);
      applyThemeDirectly(themeData);
    } catch (err) {
      console.error('Error loading theme:', err);
      setError(err);
      applyDefaultTheme();
    } finally {
      setLoading(false);
    }
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
      : null;
  };

  const adjustColor = (rgb, amount) => {
    return {
      r: Math.max(0, Math.min(255, rgb.r + amount)),
      g: Math.max(0, Math.min(255, rgb.g + amount)),
      b: Math.max(0, Math.min(255, rgb.b + amount))
    };
  };

  const rgbToString = (rgb) => `${rgb.r}, ${rgb.g}, ${rgb.b}`;

  /**
   * Generate a professional color scale with better contrast
   * Uses more sophisticated color adjustments for better results
   */
  const generateColorScale = (baseColorHex) => {
    const baseRgb = hexToRgb(baseColorHex);
    if (!baseRgb) return null;

    // More sophisticated scaling for better professional appearance
    return {
      50: adjustColor(baseRgb, 210),   // Lightest
      100: adjustColor(baseRgb, 170),
      200: adjustColor(baseRgb, 130),
      300: adjustColor(baseRgb, 90),
      400: adjustColor(baseRgb, 45),
      500: baseRgb,                     // Base color
      600: adjustColor(baseRgb, -25),   // Slightly darker for buttons
      700: adjustColor(baseRgb, -50),   // Darker for hover states
      800: adjustColor(baseRgb, -75),
      900: adjustColor(baseRgb, -100),  // Darkest
    };
  };

const applyThemeDirectly = (themeData) => {
  if (!themeData || !themeData.primaryColor) return;

  const colorScale = generateColorScale(themeData.primaryColor);
  if (!colorScale) return;

  // Remove old theme styles completely
  document.querySelectorAll('#dynamic-theme-styles').forEach(el => el.remove());

  setTimeout(() => {
    const styleTag = document.createElement('style');
    styleTag.id = 'dynamic-theme-styles';
    
    // Professional gradient-based theming
    styleTag.textContent = `
      /* Dynamic Theme Colors with Professional Gradients */
      :root {
        --color-primary-50: ${rgbToString(colorScale[50])};
        --color-primary-100: ${rgbToString(colorScale[100])};
        --color-primary-200: ${rgbToString(colorScale[200])};
        --color-primary-300: ${rgbToString(colorScale[300])};
        --color-primary-400: ${rgbToString(colorScale[400])};
        --color-primary-500: ${rgbToString(colorScale[500])};
        --color-primary-600: ${rgbToString(colorScale[600])};
        --color-primary-700: ${rgbToString(colorScale[700])};
        --color-primary-800: ${rgbToString(colorScale[800])};
        --color-primary-900: ${rgbToString(colorScale[900])};
      }

      /* Background colors - ONLY for elements explicitly using primary classes */
      .bg-primary-50:not(.btn):not(button) { background-color: rgb(${rgbToString(colorScale[50])}) !important; }
      .bg-primary-100:not(.btn):not(button) { background-color: rgb(${rgbToString(colorScale[100])}) !important; }
      .bg-primary-200:not(.btn):not(button) { background-color: rgb(${rgbToString(colorScale[200])}) !important; }
      .bg-primary-300:not(.btn):not(button) { background-color: rgb(${rgbToString(colorScale[300])}) !important; }
      .bg-primary-400:not(.btn):not(button) { background-color: rgb(${rgbToString(colorScale[400])}) !important; }
      .bg-primary-500:not(.btn):not(button) { background-color: rgb(${rgbToString(colorScale[500])}) !important; }
      
      /* Buttons with professional gradients - MAXIMUM VISIBILITY */
      .bg-primary-600,
      button.bg-primary-600,
      .btn-primary,
      .btn.btn-primary,
      a.btn-primary {
        background: linear-gradient(135deg, rgb(${rgbToString(colorScale[600])}) 0%, rgb(${rgbToString(colorScale[700])}) 100%) !important;
        color: #ffffff !important;
        font-weight: 500 !important;
        box-shadow: 0 4px 6px -1px rgba(${rgbToString(colorScale[700])}, 0.3), 0 2px 4px -1px rgba(${rgbToString(colorScale[700])}, 0.2) !important;
        border: none !important;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
      }
      
      /* Force white text on all primary buttons - HIGHEST PRIORITY */
      .btn-primary,
      .btn-primary *,
      .bg-primary-600,
      .bg-primary-600 *,
      button.bg-primary-600,
      button.bg-primary-600 *,
      a.bg-primary-600,
      a.bg-primary-600 * {
        color: #ffffff !important;
      }
      
      /* Ensure icons are white */
      .btn-primary svg,
      .bg-primary-600 svg,
      button.bg-primary-600 svg {
        color: #ffffff !important;
        fill: currentColor !important;
      }
      
      .btn-primary:hover,
      .btn.btn-primary:hover,
      button.bg-primary-600:hover,
      a.btn-primary:hover {
        background: linear-gradient(135deg, rgb(${rgbToString(colorScale[700])}) 0%, rgb(${rgbToString(colorScale[800])}) 100%) !important;
        box-shadow: 0 10px 15px -3px rgba(${rgbToString(colorScale[800])}, 0.3), 0 4px 6px -2px rgba(${rgbToString(colorScale[800])}, 0.2) !important;
        transform: translateY(-1px) !important;
        color: #ffffff !important;
      }
      
      .btn-primary:active,
      .btn.btn-primary:active,
      button.bg-primary-600:active {
        transform: scale(0.98) !important;
      }
      
      .btn-primary:disabled,
      .btn.btn-primary:disabled,
      button.bg-primary-600:disabled {
        background: linear-gradient(135deg, rgb(209, 213, 219) 0%, rgb(156, 163, 175) 100%) !important;
        color: rgb(107, 114, 128) !important;
        cursor: not-allowed !important;
        box-shadow: none !important;
        opacity: 0.6 !important;
      }
      
      .bg-primary-700,
      .hover\\:bg-primary-700:hover {
        background: linear-gradient(135deg, rgb(${rgbToString(colorScale[700])}) 0%, rgb(${rgbToString(colorScale[800])}) 100%) !important;
        color: white !important;
      }
      
      .bg-primary-800 { background-color: rgb(${rgbToString(colorScale[800])}) !important; }
      .bg-primary-900 { background-color: rgb(${rgbToString(colorScale[900])}) !important; }

      /* Text colors */
      .text-primary-50 { color: rgb(${rgbToString(colorScale[50])}) !important; }
      .text-primary-100 { color: rgb(${rgbToString(colorScale[100])}) !important; }
      .text-primary-200 { color: rgb(${rgbToString(colorScale[200])}) !important; }
      .text-primary-300 { color: rgb(${rgbToString(colorScale[300])}) !important; }
      .text-primary-400 { color: rgb(${rgbToString(colorScale[400])}) !important; }
      .text-primary-500 { color: rgb(${rgbToString(colorScale[500])}) !important; }
      .text-primary-600 { color: rgb(${rgbToString(colorScale[600])}) !important; }
      .text-primary-700 { color: rgb(${rgbToString(colorScale[700])}) !important; }
      .text-primary-800 { color: rgb(${rgbToString(colorScale[800])}) !important; }
      .text-primary-900 { color: rgb(${rgbToString(colorScale[900])}) !important; }

      /* Border colors */
      .border-primary-50 { border-color: rgb(${rgbToString(colorScale[50])}) !important; }
      .border-primary-100 { border-color: rgb(${rgbToString(colorScale[100])}) !important; }
      .border-primary-200 { border-color: rgb(${rgbToString(colorScale[200])}) !important; }
      .border-primary-300 { border-color: rgb(${rgbToString(colorScale[300])}) !important; }
      .border-primary-400 { border-color: rgb(${rgbToString(colorScale[400])}) !important; }
      .border-primary-500 { border-color: rgb(${rgbToString(colorScale[500])}) !important; }
      .border-primary-600 { border-color: rgb(${rgbToString(colorScale[600])}) !important; }
      .border-primary-700 { border-color: rgb(${rgbToString(colorScale[700])}) !important; }
      .border-primary-800 { border-color: rgb(${rgbToString(colorScale[800])}) !important; }
      .border-primary-900 { border-color: rgb(${rgbToString(colorScale[900])}) !important; }

      /* Hover states - specific selectors */
      .hover\\:bg-primary-50:hover:not(button) { background-color: rgb(${rgbToString(colorScale[50])}) !important; }
      .hover\\:bg-primary-100:hover:not(button) { background-color: rgb(${rgbToString(colorScale[100])}) !important; }
      .hover\\:bg-primary-200:hover:not(button) { background-color: rgb(${rgbToString(colorScale[200])}) !important; }
      .hover\\:bg-primary-300:hover:not(button) { background-color: rgb(${rgbToString(colorScale[300])}) !important; }
      
      .hover\\:text-primary-600:hover { color: rgb(${rgbToString(colorScale[600])}) !important; }
      .hover\\:text-primary-700:hover { color: rgb(${rgbToString(colorScale[700])}) !important; }
      .hover\\:border-primary-300:hover { border-color: rgb(${rgbToString(colorScale[300])}) !important; }

      /* Focus ring */
      .focus\\:ring-primary-500:focus {
        --tw-ring-color: rgb(${rgbToString(colorScale[500])}) !important;
      }

      /* Professional gradient backgrounds - for headers and cards */
      .from-primary-500 { --tw-gradient-from: rgb(${rgbToString(colorScale[500])}) !important; }
      .from-primary-600 { --tw-gradient-from: rgb(${rgbToString(colorScale[600])}) !important; }
      .from-primary-700 { --tw-gradient-from: rgb(${rgbToString(colorScale[700])}) !important; }
      .from-primary-800 { --tw-gradient-from: rgb(${rgbToString(colorScale[800])}) !important; }
      .from-primary-900 { --tw-gradient-from: rgb(${rgbToString(colorScale[900])}) !important; }
      
      .via-primary-500 { --tw-gradient-via: rgb(${rgbToString(colorScale[500])}) !important; }
      .via-primary-600 { --tw-gradient-via: rgb(${rgbToString(colorScale[600])}) !important; }
      .via-primary-700 { --tw-gradient-via: rgb(${rgbToString(colorScale[700])}) !important; }
      .via-primary-800 { --tw-gradient-via: rgb(${rgbToString(colorScale[800])}) !important; }
      .via-primary-900 { --tw-gradient-via: rgb(${rgbToString(colorScale[900])}) !important; }
      
      .to-primary-500 { --tw-gradient-to: rgb(${rgbToString(colorScale[500])}) !important; }
      .to-primary-600 { --tw-gradient-to: rgb(${rgbToString(colorScale[600])}) !important; }
      .to-primary-700 { --tw-gradient-to: rgb(${rgbToString(colorScale[700])}) !important; }
      .to-primary-800 { --tw-gradient-to: rgb(${rgbToString(colorScale[800])}) !important; }
      .to-primary-900 { --tw-gradient-to: rgb(${rgbToString(colorScale[900])}) !important; }

      /* Enhanced shadow colors for depth */
      .shadow-primary-lg {
        box-shadow: 0 10px 15px -3px rgba(${rgbToString(colorScale[700])}, 0.3), 
                    0 4px 6px -2px rgba(${rgbToString(colorScale[700])}, 0.2) !important;
      }

      .shadow-primary-xl {
        box-shadow: 0 20px 25px -5px rgba(${rgbToString(colorScale[800])}, 0.3), 
                    0 10px 10px -5px rgba(${rgbToString(colorScale[800])}, 0.2) !important;
      }
    `;

    document.head.appendChild(styleTag);

    localStorage.setItem('appTheme', JSON.stringify(themeData));
  }, 10);
};

  const applyDefaultTheme = () => {
    const defaultTheme = {
      serviceType: 'Default',
      primaryColor: '#3b82f6',
      displayName: 'Default'
    };
    applyThemeDirectly(defaultTheme);
  };

  return {
    theme,
    loading,
    error,
    jobTermSingular: theme?.jobTermSingular || 'Job',
    jobTermPlural: theme?.jobTermPlural || 'Jobs',
    customerTerm: theme?.customerTerm || 'Customer',
    welcomeMessage: theme?.dashboardWelcomeMessage || 'Welcome back! Here\'s your business overview.',
  };
};

export default useTheme;